
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { UploadContextProps } from './types';
import { useUploadType } from '../hooks/useUploadType';
import { useUploadMetadata } from '../hooks/useUploadMetadata';
import { useUploadSubmit } from '../hooks/useUploadSubmit';
import { useReviewRequest } from '../hooks/useReviewRequest';
import { useVisibility } from '../hooks/useVisibility';
import { useFormPersistence } from '../hooks/useFormPersistence';
import type { EmailChip } from '@/components/reviews/request/EmailChipInput';
import {
  PENDING_UPLOAD_SUBMIT_KEY,
  clearPendingIntent,
  peekPendingIntent,
} from '@/lib/auth/pendingIntent';

const UploadContext = createContext<UploadContextProps | undefined>(undefined);

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const currentUserId = user?.id || '';
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Email-invitation state for the upload flow
  const [inviteChips, setInviteChips] = useState<EmailChip[]>([]);
  const [invitePersonalNote, setInvitePersonalNote] = useState('');
  const [inviteWarranty, setInviteWarranty] = useState(false);
  
  // Hook for handling upload type and method
  const {
    visibilityChoice,
    setVisibilityChoice,
    sourceChoice,
    setSourceChoice,
    uploadMethod,
    setUploadMethod,
    uploadType,
    file,
    setFile,
    handleTabChange
  } = useUploadType();
  
  // Hook for handling visibility and AI reviews
  const {
    visibility,
    setVisibility,
    aiReviewUsage
  } = useVisibility();
  
  // Hook for handling metadata
  const {
    title,
    setTitle,
    abstract,
    setAbstract,
    category,
    setCategory,
    keywords,
    setKeywords,
    additionalAuthors,
    setAdditionalAuthors,
    arxivUrl,
    setArxivUrl,
    externalUrl,
    setExternalUrl,
    isValidatingUrl,
    isUrlValid,
    urlValidationMessage,
    arxivMetadata,
    validateExternalUrl,
    license,
    licenseCompliant,
    licenseValidationError,
    duplicateInfo,
    publishedAt
  } = useUploadMetadata();
  
  // Hook for handling review requests
  const {
    requestReview,
    setRequestReview,
    selectedReviewers,
    setSelectedReviewers,
    allUsers
  } = useReviewRequest();
  
  // Form persistence hook
  const { restoreFormData, clearFormData, hasPendingFormData } = useFormPersistence({
    formData: {
      visibilityChoice,
      sourceChoice,
      uploadMethod,
      visibility,
      externalUrl,
      arxivUrl,
      title,
      abstract,
      category,
      keywords,
      additionalAuthors,
      requestReview,
      selectedReviewers,
    },
    setters: {
      setVisibilityChoice,
      setSourceChoice,
      setUploadMethod,
      setVisibility,
      setExternalUrl,
      setArxivUrl,
      setTitle,
      setAbstract,
      setCategory,
      setKeywords,
      setAdditionalAuthors,
      setRequestReview,
      setSelectedReviewers,
    }
  });
  
  // Hook for handling form submission
  const {
    handleSubmit,
    isSubmitting,
    handleAuthSuccess: submitAuthSuccess,
    pendingSubmit
  } = useUploadSubmit({
    title,
    abstract,
    category,
    keywords,
    additionalAuthors,
    uploadMethod,
    file,
    arxivUrl,
    licenseCompliant,
    visibility,
    requestReview,
    selectedReviewers,
    publishedAt,
    inviteChips,
    invitePersonalNote,
    inviteWarranty,
    onRequireAuth: () => setShowAuthModal(true),
    clearFormData
  });
  
  // Restore form data on mount (only for auth flow, not every visit)
  useEffect(() => {
    const pendingUpload = peekPendingIntent(PENDING_UPLOAD_SUBMIT_KEY);
    
    // Only restore if user was in the middle of an auth flow
    // This prevents skipping the visibility choice on fresh visits
    if (pendingUpload === 'true' && hasPendingFormData()) {
      restoreFormData();
      if (user) {
        clearPendingIntent(PENDING_UPLOAD_SUBMIT_KEY);
        // Give time for form state to restore
        setTimeout(() => {
          submitAuthSuccess();
        }, 200);
      }
    }
    // If no pending auth flow, start fresh - don't restore
  }, [user]); // Only run on mount and when user changes
  
  // Handle auth success - auto-submit if there was a pending submission
  const handleAuthSuccess = useCallback(() => {
    submitAuthSuccess();
  }, [submitAuthSuccess]);
  
  // Show metadata form only for file uploads (arXiv is ground truth)
  const shouldShowMetadataForm = uploadMethod === 'file';
  
  // Derived validation status - only show invalid after actual validation attempt
  const validationStatus = 
    isValidatingUrl ? 'validating' : 
    isUrlValid ? 'valid' : 
    arxivUrl && !isUrlValid ? 'invalid' : 'idle';
  
  // Empty validation errors array (maintained for API compatibility)
  const validationErrors: string[] = [];

  // Combine all props for the context
  const value: UploadContextProps = {
    visibilityChoice,
    setVisibilityChoice,
    sourceChoice,
    setSourceChoice,
    uploadMethod,
    setUploadMethod,
    file,
    setFile,
    arxivUrl,
    setArxivUrl,
    externalUrl,
    setExternalUrl,
    title,
    setTitle,
    abstract,
    setAbstract,
    category,
    setCategory,
    keywords,
    setKeywords,
    additionalAuthors,
    setAdditionalAuthors,
    visibility,
    setVisibility,
    aiReviewUsage,
    requestReview,
    setRequestReview,
    selectedReviewers,
    setSelectedReviewers,
    handleSubmit,
    isSubmitting,
    isValidatingUrl,
    shouldShowMetadataForm,
    uploadType,
    validationStatus,
    validationMessage: urlValidationMessage,
    validationErrors,
    validateExternalUrl,
    metadata: arxivMetadata,
    handleTabChange,
    allUsers,
    currentUserId,
    license,
    licenseCompliant,
    licenseValidationError,
    duplicateInfo,
    // Auth modal for unsigned users
    showAuthModal,
    setShowAuthModal,
    handleAuthSuccess,
    clearFormData,
    // Email-based reviewer invitations
    inviteChips,
    setInviteChips,
    invitePersonalNote,
    setInvitePersonalNote,
    inviteWarranty,
    setInviteWarranty,
  };
  
  return (
    <UploadContext.Provider value={value}>
      {children}
    </UploadContext.Provider>
  );
};

export const useUploadContext = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUploadContext must be used within an UploadProvider');
  }
  return context;
};
